import React from 'react';
import { 
  TextField, FormControl, InputLabel, Select, MenuItem, 
  FormHelperText, InputAdornment, IconButton, Box, Typography, Chip,
  styled
} from '@mui/material';
import { MyLocation, Add as AddIcon, AttachFile } from '@mui/icons-material';

const StyledTextField = styled(TextField)(() => ({
  width: 250,

  '& .MuiInputLabel-root': {
    color: '#000000',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#000000',
    },
    '&.Mui-error': {
      color: '#d32f2f',
    }
  },

  '& .MuiInputLabel-asterisk': {
    color: '#d32f2f !important',
    fontSize: '1rem !important',
    verticalAlign: 'top'
  },

  '& .MuiInputBase-input': {
    color: '#000000',
    '&::placeholder': {
      color: '#00000080',
      opacity: 1,
    }
  },

  // ✅ OUTLINE FIX (default + hover + focus)
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#00000040', // default
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#09772d', // hover green
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#09772d', // focus green
      borderWidth: '2px',
    }
  }
}));

const StyledFormControl = styled(FormControl)(() => ({
  width: 250,

  '& .MuiInputLabel-root': {
    color: '#000000',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#000000',
    },
    '&.Mui-error': {
      color: '#d32f2f',
    }
  },

  '& .MuiInputLabel-asterisk': {
    color: '#d32f2f !important',
    fontSize: '1rem !important',
    verticalAlign: 'top'
  },

  // ✅ Apply same outline behavior to Select
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#00000040',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#09772d',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#09772d',
      borderWidth: '2px',
    }
  }
}));

const DynamicField = ({
  field,
  value,
  onChange,
  error = false,
  helperText = '',
  loading = false,
  options = [],
  onAddOption,
  onGeocode,
  showMap = false,
  files = [],
  onFileChange,
  onFileRemove,
  mapEnabled = false,
  readOnly = false,
  ...props
}) => {
  const isMapField = field.name === 'complete_address';
  const isFileField = field.type === 'file';
  const isSelectField = field.type === 'select';

  if (isFileField) {
    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ color: '#000000', fontWeight: 600 }}>
          {field.label} <AttachFile fontSize="small" sx={{ ml: 1, color: '#ffc022' }} />
        </Typography>

        <input
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={onFileChange}
          style={{ marginTop: '8px' }}
          disabled={loading}
        />

        {files.length > 0 && (
          <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
            {files.map((file, index) => (
              <Chip
                key={index}
                label={file.name}
                onDelete={() => onFileRemove(index)}
                size="small"
                sx={{
                  bgcolor: '#ffc022',
                  color: '#000000',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': { color: '#000000' }
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  if (isSelectField) {
    return (
      <StyledFormControl fullWidth error={error} size="small">
        <InputLabel>
          {field.label} {field.required && '*'}
        </InputLabel>

        <Select
          name={field.name}
          value={value || ''}
          onChange={onChange}
          label={field.label}
          readOnly={readOnly}
          endAdornment={
            onAddOption ? (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={onAddOption}
                  disabled={loading}
                  edge="end"
                  sx={{ color: '#ffc022' }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          {...props}
        >
          {options.map(option => (
            <MenuItem key={option.id || option.value} value={option.id || option.value}>
              {option.name || option.label}
            </MenuItem>
          ))}
        </Select>

        {error && (
          <FormHelperText error sx={{ color: '#d32f2f' }}>
            {helperText}
          </FormHelperText>
        )}
      </StyledFormControl>
    );
  }

  return (
    <StyledTextField
      fullWidth
      label={field.label}
      name={field.name}
      value={value || ''}
      onChange={onChange}
      error={error}
      helperText={
        helperText || 
        (isMapField && showMap ? "📍 Click map or drag marker" : field.helperText)
      }
      required={field.required}
      size="small"
      type={field.type || 'text'}
      multiline={field.multiline}
      rows={field.rows || 3}
      maxRows={field.maxRows || 5}
      InputProps={{
        readOnly,
        endAdornment: isMapField && mapEnabled ? (
          <InputAdornment position="end">
            <IconButton 
              size="small"
              onClick={onGeocode}
              disabled={!value?.trim() || loading}
              sx={{ color: '#ffc022' }}
            >
              <MyLocation />
            </IconButton>
          </InputAdornment>
        ) : null
      }}
      sx={{
        '& .MuiFormHelperText-root': {
          color: error ? '#d32f2f' : '#00000080'
        }
      }}
      {...props}
    />
  );
};

export default DynamicField;